import { createContext, useContext, useState, useEffect } from 'react';
import { isAuthenticated, signIn, signOut, getUser, handleCallback, getAccessToken } from '../auth/oidcClient';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [isAuthenticatedState, setIsAuthenticatedState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      
      const authenticated = await Promise.race([
        isAuthenticated(),
        new Promise((resolve) => {
          setTimeout(() => resolve(false), 10000);
        })
      ]);
      
      setIsAuthenticatedState(authenticated);
      
      if (authenticated) {
        try {
          const userData = await Promise.race([
            getUser(),
            new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Timeout')), 5000);
            })
          ]);
          
          if (userData?.profile) {
            let role = userData.profile.role || 
                        userData.profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 
                        'Manager';
            
            // Normalizar role: converter primeira letra para maiúscula se necessário
            // Mas manter roles específicas como 'afericoes.admin' como estão
            if (role && !role.includes('.')) {
              role = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
            }
            
            const userInfo = {
              sub: userData.profile.sub,
              name: userData.profile.name,
              email: userData.profile.email,
              role: role,
            };
            
            setUser(userInfo);
            
            // Atualizar localStorage
            localStorage.setItem('auth_user', JSON.stringify(userInfo));
          }
        } catch (userError) {
          // Tentar obter do localStorage
          const storedUser = localStorage.getItem('auth_user');
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch (e) {
              console.warn('Erro ao parsear usuário do localStorage:', e);
            }
          }
        }
      } else {
        // Tentar obter do localStorage como fallback
        const storedUser = localStorage.getItem('auth_user');
        if (storedUser) {
          try {
            setUser(JSON.parse(storedUser));
          } catch (e) {
            console.warn('Erro ao parsear usuário do localStorage:', e);
          }
        }
      }
    } catch (error) {
      console.error('[AuthContext] Erro ao verificar autenticação:', error);
      setIsAuthenticatedState(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async () => {
    await signIn();
  };

  const logout = async () => {
    await signOut();
    setIsAuthenticatedState(false);
    setUser(null);
  };

  const handleAuthCallback = async () => {
    try {
      const userData = await handleCallback();
      if (userData?.profile) {
        let role = userData.profile.role || 
                    userData.profile['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 
                    'Manager';
        
        // Normalizar role: converter primeira letra para maiúscula se necessário
        // Mas manter roles específicas como 'afericoes.admin' como estão
        if (role && !role.includes('.')) {
          role = role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
        }
        
        const userInfo = {
          sub: userData.profile.sub,
          name: userData.profile.name,
          email: userData.profile.email,
          role: role,
        };
        
        setUser(userInfo);
        setIsAuthenticatedState(true);
        
        localStorage.setItem('auth_user', JSON.stringify(userInfo));
      }
      return userData;
    } catch (error) {
      console.error('Erro no callback de autenticação:', error);
      throw error;
    }
  };

  const getToken = async () => {
    return await getAccessToken();
  };

  const value = {
    isAuthenticated: isAuthenticatedState,
    isLoading,
    user,
    login,
    logout,
    handleAuthCallback,
    getAccessToken: getToken,
    refresh: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
};
